// 分享到第三方的共用邏輯：步道詳情的分享列與投票頁的分享加碼都走這裡。
//
// 兩頁分享的東西不一樣（一個是步道、一個是活動頁），但踩過的坑是同一組，
// 複製兩份遲早會有一份沒跟上。
export function useShare() {
  const SHARE_URL = {
    // LINE 的網頁分享只有 social-plugins 這一支。**不要改成 `line.me/R/share`**：
    // 那是 App 的 deep link scheme，桌機瀏覽器沒有 LINE 接手時會被 302 到
    // line.me 再轉到 www.line.me/en/ 的行銷首頁，使用者根本看不到分享畫面。
    // 這條規矩只管桌機——手機反過來不能用這一支（見 share() 的 LINE 分支）
    line: (url, text) =>
      `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    facebook: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
  }

  function openShareWindow(target) {
    // features 一帶 noopener，window.open 就固定回 null，分不出是「開了」還是
    // 「被擋掉」，本頁也跟著跳走。改成拿得到參考再自己切斷 opener
    const win = window.open(target, '_blank')
    if (win) win.opener = null
    // 真的被彈跳視窗擋下時退回同分頁開啟，按了至少要有反應
    else window.location.href = target
  }

  async function copyLink(url) {
    try {
      await navigator.clipboard.writeText(url)
      return true
    } catch {
      return false
    }
  }

  // 桌機的 Safari / Edge 也有 navigator.share，只看有沒有面板會把桌機好好的 sharer 換掉，
  // 所以另外認手機。iPadOS 的 UA 冒充成 Mac，靠觸控點數分辨
  function isMobile() {
    const ua = navigator.userAgent || ''
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true
    return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
  }

  /**
   * 叫出系統分享面板
   * @returns {Promise<boolean|null>} true 分享了、false 使用者按取消、null 面板叫不出來（該走退路）
   */
  async function shareViaSheet(data) {
    if (!navigator.share) return null
    try {
      await navigator.share(data)
      return true
    } catch (err) {
      // 使用者自己按取消不是錯誤，不要再退到其他路上跳提示或開視窗
      return err?.name === 'AbortError' ? false : null
    }
  }

  /**
   * 分享到指定平台
   * @param {string} key line / facebook / link
   * @param {object} opts copyText 為複製時要放進剪貼簿的整段文字（沒給就只複製網址）
   * @returns {Promise<boolean>} 使用者是否真的走完分享動作（系統面板按取消為 false）
   */
  async function share(key, { url, text, notify, copyHint, copyText }) {
    // 手機上的 Facebook 不走 sharer.php：FB 手機網頁版發文時會把連結附件丟掉
    // （貼文只剩打的字），sharer 頁上的「在 App 中打開」也只會開到 App 首頁，
    // 分享內容帶不過去。系統面板選 Facebook 開的是 App 原生的分享畫面，連結一定帶得進去；
    // 面板叫不出來（部分 App 內建瀏覽器）才退回 sharer
    // 只交網址：iOS 的 Facebook 分享擴充收到文字與網址兩樣時只接文字、把連結丟掉
    // （FB 本來也不收預填文字），交給它的東西只能有網址這一樣
    if (key === 'facebook' && isMobile()) {
      const shared = await shareViaSheet({ url })
      if (shared !== null) return shared
    }

    // 手機上的 LINE 不走 lineit/share：那一頁要先登入 LINE，access.line.me 會叫起 App
    // 自動登入、App 再把人丟回瀏覽器，瀏覽器又叫一次 App，兩邊無限來回跳、永遠到不了
    // 分享畫面（踩過，錄到影片）。手機改用 App 的 deep link 直接開選聊天室的畫面；
    // 它只收 text，網址得接在文字後面。同分頁導過去，universal link 才會交給 App 接手
    if (key === 'line' && isMobile()) {
      const body = text ? `${text}\n${url}` : url
      window.location.href = `https://line.me/R/share?text=${encodeURIComponent(body)}`
      return true
    }

    if (key !== 'link') {
      openShareWindow(SHARE_URL[key](url, text))
      // LINE 與 Facebook 開的是新視窗，之後發生什麼事這一頁就不知道了，
      // 只能認「按了分享」——這也是後端加碼一天只認一次的原因
      return true
    }

    const copied = await copyLink(copyText || url)
    notify?.(
      copied
        ? copyHint || '已複製連結,快分享給朋友吧!'
        : '這個瀏覽器不支援複製,請直接複製網址列的連結'
    )
    return copied
  }

  return { SHARE_URL, openShareWindow, copyLink, share }
}
